import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ProtectedRoute, setUnauthorizedHandler } from '@/auth';
import { PageLayout } from '@/components/PageLayout';
import { Toaster } from '@/components/ui/toaster';
import Login from '@/pages/auth/Login';
import Login2FA from '@/pages/auth/Login2FA';
import Signup from '@/pages/auth/Signup';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import PatientList from '@/pages/patients/PatientList';
import PatientShell from '@/pages/patients/PatientShell';
import GlobalResources from '@/pages/resources/GlobalResources';
import Clinicians from '@/pages/clinicians/Clinicians';
import CgmReport from '@/pages/cgm/CgmReport';
import Profile from '@/pages/account/Profile';
import NotFound from '@/pages/NotFound';
import Forbidden from '@/pages/Forbidden';

const Logout = () => {
  const navigate = useNavigate();
  useEffect(() => {
    import('@/auth').then(({ useAuthStore }) => {
      useAuthStore.getState().logout().finally(() => navigate('/login', { replace: true }));
    });
  }, [navigate]);
  return null;
};

export default function App() {
  const navigate = useNavigate();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (window.location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    });
    // No app-level forbidden handler. A 403 from a sub-resource (e.g. report
    // metrics on the patient summary) used to navigate the whole app to
    // /forbidden, knocking the user off the page. Pages that want a 403 on
    // their *primary* fetch to navigate can install one locally.
  }, [navigate]);

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/patients" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/2fa" element={<Login2FA />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/forbidden" element={<Forbidden />} />
          <Route
            path="/patients"
            element={<PageLayout><PatientList /></PageLayout>}
          />
          <Route
            path="/patients/:id/*"
            element={<PageLayout><PatientShell /></PageLayout>}
          />
          <Route
            path="/resources"
            element={<PageLayout><GlobalResources /></PageLayout>}
          />
          <Route
            path="/clinicians"
            element={<PageLayout><Clinicians /></PageLayout>}
          />
          <Route
            path="/account"
            element={<PageLayout><Profile /></PageLayout>}
          />
          <Route path="/cgm-report/:patientId" element={<CgmReport />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
    </>
  );
}
